import { useState, useEffect, useCallback } from "react";
import { getApiBaseUrl } from "../utils/apiBase";

// Use centralized API configuration
const API_BASE_URL = getApiBaseUrl();

// Cache for lookups to avoid repeated API calls
const lookupCache = {
  industries: null,
  locations: null,
  companySizes: null,
  revenueRanges: null,
  managementLevels: null,
  jobFunctions: null,
};

export const useLookups = () => {
  const [lookupData, setLookupData] = useState({
    industries: [],
    locations: [],
    companySizes: [],
    revenueRanges: [],
    managementLevels: [],
    jobFunctions: [],
  });

  const [loading, setLoading] = useState({
    industries: false,
    locations: false,
    companySizes: false,
    revenueRanges: false,
    managementLevels: false,
    jobFunctions: false,
  });

  const loadLookup = useCallback(async (type) => {
    // Return cached data if available
    if (lookupCache[type]) {
      return lookupCache[type];
    }

    setLoading((prev) => ({ ...prev, [type]: true }));

    try {
      let url;
      let processData;

      switch (type) {
        case "industries":
          url = `${API_BASE_URL}/api/ai/lookups/industries`;
          processData = (data) => {
            if (data.success && data.items && Array.isArray(data.items)) {
              return data.items.map((item) => ({
                value: item.id || item.code,
                label: item.name,
                description: item.description,
              }));
            }
            return [];
          };
          break;

        case "locations":
          url = `${API_BASE_URL}/api/ai/lookups/locations`;
          processData = (data) => {
            if (data.success && data.items && Array.isArray(data.items)) {
              // Remove duplicates and add unique keys
              const locations = data.items.map((loc, index) => ({
                value: loc.id,
                label: loc.name + (loc.type ? ` (${loc.type})` : ""),
                type: loc.type,
                uniqueKey: `${loc.id}-${loc.type || "unknown"}-${index}`,
              }));

              // Remove true duplicates (same id AND type)
              return locations.filter(
                (loc, index, arr) =>
                  arr.findIndex(
                    (l) => l.value === loc.value && l.type === loc.type
                  ) === index
              );
            }
            return [];
          };
          break;

        case "companySizes":
          url = `${API_BASE_URL}/api/ai/lookups/company-sizes`;
          processData = (data) => {
            if (data.success && data.items && Array.isArray(data.items)) {
              return data.items.map((size) => ({
                value: `${size.minEmployees}-${size.maxEmployees || ""}`,
                label: size.name,
                min: size.minEmployees,
                max: size.maxEmployees,
              }));
            }
            return [];
          };
          break;

        case "revenueRanges":
          url = `${API_BASE_URL}/api/ai/lookups/revenue-ranges`;
          processData = (data) => {
            if (data.success && data.data && Array.isArray(data.data)) {
              return data.data.map((range) => ({
                value: range.value || range.id,
                label: range.label || range.name,
              }));
            }
            return [];
          };
          break;

        case "managementLevels":
          url = `${API_BASE_URL}/api/ai/lookups/management-levels`;
          processData = (data) => {
            if (data.success && data.items && Array.isArray(data.items)) {
              return data.items.map((level) => ({
                value: level.id || level.Id,
                label: level.name || level.Name,
              }));
            }
            return [];
          };
          break;

        case "jobFunctions":
          url = `${API_BASE_URL}/api/ai/lookups/job-functions`;
          processData = (data) => {
            if (data.success && data.items && Array.isArray(data.items)) {
              return data.items.map((func) => ({
                value: func.id || func.Id,
                label: func.name || func.Name,
              }));
            }
            return [];
          };
          break;

        default:
          throw new Error(`Unknown lookup type: ${type}`);
      }

      const response = await fetch(url);
      const data = await response.json();
      const processedData = processData(data);

      // Cache the result
      lookupCache[type] = processedData;

      setLookupData((prev) => ({ ...prev, [type]: processedData }));
      return processedData;
    } catch (error) {
      console.error(`Failed to load ${type}:`, error);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  }, []);

  const loadIndustries = useCallback(
    () => loadLookup("industries"),
    [loadLookup]
  );
  const loadLocations = useCallback(
    () => loadLookup("locations"),
    [loadLookup]
  );
  const loadCompanySizes = useCallback(
    () => loadLookup("companySizes"),
    [loadLookup]
  );
  const loadRevenueRanges = useCallback(
    () => loadLookup("revenueRanges"),
    [loadLookup]
  );
  const loadManagementLevels = useCallback(
    () => loadLookup("managementLevels"),
    [loadLookup]
  );
  const loadJobFunctions = useCallback(
    () => loadLookup("jobFunctions"),
    [loadLookup]
  );

  // Load cached data on mount
  useEffect(() => {
    Object.keys(lookupCache).forEach((type) => {
      if (lookupCache[type]) {
        setLookupData((prev) => ({ ...prev, [type]: lookupCache[type] }));
      }
    });
  }, []);

  return {
    lookupData,
    loading,
    loadIndustries,
    loadLocations,
    loadCompanySizes,
    loadRevenueRanges,
    loadManagementLevels,
    loadJobFunctions,
  };
};
